import { FormControlLabel, Grid, Typography } from "@mui/material";
import Checkbox from "@mui/material/Checkbox";
import { useTranslation } from "react-i18next";
import { CURRENCY } from "../../../../constants/Variables";
import globalStyle from "../../../../globalStyle";
import {
  calculateLossAmount,
  calculateProductFinalPrice,
  calculateVat,
  handleOpenSnackBar,
  roundTo2Decimal,
} from "../../../../utils";
import Label from "../../../../components/Label";
import TextInput from "../../../../components/TextInputs";
import GlobalButton from "../../../../components/CustomButton";

const RestockPricing = ({
  inputValues,
  setInputValues,
  inputError,
  setInputError,
  vat,
  isPriceApplied,
  setIsPriceApplied,
  isQuantityApplied,
  setIsQuantityApplied,
  onSave,
}) => {
  const { t } = useTranslation(["products", "common"]);

  const handleChangeValues = (event) => {
    const { name, type, valueAsNumber, value } = event.target;

    const formattedValue = type === "number" ? valueAsNumber : value;

    const priceAfterMargin =
      inputValues.price + (inputValues.price * inputValues.profitMargin) / 100;

    if (name === "discount" && formattedValue > priceAfterMargin) {
      handleOpenSnackBar("error", t("validation.discountCannotExceedPrice"));
      return;
    }

    setInputValues((prev) => ({ ...prev, [name]: formattedValue }));
    setInputError((prev) => ({ ...prev, [name]: "" }));
  };

  const finalPrice = calculateProductFinalPrice(
    inputValues.price,
    inputValues.discount,
    inputValues.profitMargin,
  );

  const vatAmount = calculateVat(finalPrice, vat);

  const lossAmount = calculateLossAmount(
    inputValues.price,
    inputValues.discount,
    inputValues.profitMargin,
  );

  const finalPriceWithVat = roundTo2Decimal(finalPrice + vatAmount);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography fontSize={20} fontWeight={600}>
          {t("pricingAndStocks")}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={isQuantityApplied}
              onChange={() => {
                setIsQuantityApplied(!isQuantityApplied);
                setInputError((prev) => ({
                  ...prev,
                  quantity: "",
                }));
              }}
            />
          }
          label={
            t("updateQuantity")
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isPriceApplied}
              onChange={() => {
                setIsPriceApplied(!isPriceApplied);
                setInputError((prev) => ({
                  ...prev,
                  price: "",
                  profitMargin: "",
                  discount: "",
                  quantity: "",
                }));
              }}
            />
          }
          label={
            t("updatePriceProfitMarginDiscount")
          }
        />
      </Grid>
      {isQuantityApplied && (
        <Grid item xs={12} md={4}>
          <Label
            required
            labelText={t("quantity")}
            htmlFor={"product-quantity"}
          />
          <TextInput
            id={"product-quantity"}
            styleProps={globalStyle.textField}
            placeholder={t("enterQuantity")}
            type={"number"}
            name={"quantity"}
            value={inputValues.quantity}
            onChange={handleChangeValues}
            inputError={Boolean(inputError.quantity)}
            helperText={t(inputError.quantity)}
          />
        </Grid>
      )}
      {isPriceApplied && (
        <>
          <Grid item xs={12} md={4}>
            <Label
              labelText={t("purchasePriceWithoutVat")}
              required
              htmlFor={"product-price"}
            />
            <TextInput
              id={"product-price"}
              styleProps={globalStyle.textField}
              placeholder={t("enterPurchasePriceWithoutVat")}
              type={"number"}
              name={"price"}
              value={inputValues.price}
              onChange={handleChangeValues}
              InputEndIcon={CURRENCY}
              inputError={Boolean(inputError.price)}
              helperText={t(inputError.price)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label
              optional
              labelText={t("profitMargin")}
              htmlFor={"product-profit-margin"}
            // required
            />
            <TextInput
              id={"product-profit-margin"}
              styleProps={globalStyle.textField}
              placeholder={t("enterProfitMargin")}
              type={"number"}
              name={"profitMargin"}
              value={inputValues.profitMargin}
              onChange={handleChangeValues}
              InputEndIcon={"%"}
              inputError={Boolean(inputError.profitMargin)}
              helperText={t(inputError.profitMargin)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label
              labelText={t("discount")}
              htmlFor={"product-discount"}
              optional
            />
            <TextInput
              id={"product-discount"}
              styleProps={globalStyle.textField}
              placeholder={t("enterDiscount")}
              type={"number"}
              name={"discount"}
              value={inputValues.discount}
              onChange={handleChangeValues}
              InputEndIcon={CURRENCY}
              inputError={Boolean(inputError.discount)}
              helperText={t(inputError.discount)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Label
              labelText={t("finalPrice")}
              htmlFor={"product-final-price"}
            />
            <TextInput
              id={"product-final-price"}
              styleProps={globalStyle.textField}
              readonly
              InputEndIcon={CURRENCY}
              value={finalPrice}
              inputError={finalPrice < 0}
              helperText={
                finalPrice < 0
                  ? t("validation.finalPriceCannotBeZeroOrNegative")
                  : null
              }
            />
            {lossAmount < 0 && (
              <Typography variant="body2" color="warning.main">
                {t("validation.youAreLosing", {
                  amount: lossAmount,
                  currency: CURRENCY,
                })}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Label
              labelText={t("vatAmount", { vat: vat })}
              htmlFor={"product-vat"}
            />
            <TextInput
              id={"product-vat"}
              styleProps={globalStyle.textField}
              readonly
              InputEndIcon={CURRENCY}
              value={vatAmount}
              inputError={vatAmount < 0}
              helperText={
                vatAmount < 0
                  ? t("validation.vatAmountCannotBeZeroOrNegative")
                  : null
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label
              labelText={t("finalPriceWithVat")}
              htmlFor={"product-final-price-with-vat"}
            />
            <TextInput
              id={"product-final-price-with-vat"}
              styleProps={globalStyle.textField}
              readonly
              InputEndIcon={CURRENCY}
              value={finalPriceWithVat}
              inputError={finalPriceWithVat < 0}
              helperText={
                finalPriceWithVat < 0
                  ? t("validation.finalPriceWithVatCannotBeZeroOrNegative")
                  : null
              }
            />
          </Grid>
        </>
      )}
      {(isQuantityApplied || isPriceApplied) && (
        <Grid item xs={12} md={4} display="flex" justifyContent="flex-end">
          <GlobalButton
            btnText={"updated"}
            handleClickBtn={onSave}
            styleProps={{
              width: "100%",
              height: 40,
              alignSelf: "flex-end",
            }}
          />
        </Grid>
      )}
    </Grid>
  );
};

export default RestockPricing;
